import { ErrorResponse } from "../domain/error.interface";

/**
 * The human-readable message out of a rejected API call.
 *
 * WHY THIS EXISTS. The backends do not agree on a single error shape, and the previous inline
 * extraction — `error?.errorMessage || message?.[0]` — silently corrupted one of them:
 *
 *  - `BaseResponse.buildError({ errorMessage })` (HttpExceptionFilter's string path)
 *      -> { error: { errorMessage: "..." } }              read correctly
 *  - class-validator / ValidationPipe
 *      -> { message: ["...", "..."] }                     read correctly via message[0]
 *  - `new BadRequestException("some text")` (a hand-thrown guard, e.g. SIMS-7491's
 *    structural-override refusal). `getResponse()` returns an OBJECT, so the filter's
 *    string branch is skipped and Nest's own shape is returned verbatim:
 *      -> { statusCode, message: "some text", error: "Bad Request" }
 *    Here `message` is a STRING, so `message?.[0]` yields its FIRST CHARACTER — the user saw
 *    a snackbar reading "C".
 *
 * Any guard that explains itself in prose hits that third case, which is precisely when the
 * message matters most: it is the only thing telling the user what to change.
 */
export function apiErrorMessage(
	error: unknown,
	fallback = "An error occurred",
): string {
	const response = error as
		| (Omit<ErrorResponse, "message"> & { message?: string | string[] })
		| undefined;

	const structured = response?.error?.errorMessage;
	if (typeof structured === "string" && structured.trim()) {
		return structured;
	}

	const message = response?.message;
	if (typeof message === "string" && message.trim()) {
		return message;
	}
	if (Array.isArray(message)) {
		const first = message.find(
			(entry) => typeof entry === "string" && entry.trim(),
		);
		if (first) {
			return first;
		}
	}

	return fallback;
}

/**
 * The dot-paths of every LEAF field react-hook-form rejected.
 *
 * `Object.keys(errors)` is not enough: for a nested field it returns only the top-level key —
 * `"treatmentConfig"` — so a message built from it names the object, not the field the user has to
 * fix. That matters most in exactly the case this was written for: a required-but-hidden
 * `treatmentConfig.duration` blocking a Custom Plan submit with nothing visible on screen.
 *
 * Reads only `message`/`type` and whether a `ref` exists, so no field VALUES are copied out — the
 * form carries a participant's name, date of birth and Client ID Number.
 */
export function validationFieldPaths(errors: unknown, prefix = ""): string[] {
	if (!errors || typeof errors !== "object") {
		return [];
	}
	const paths: string[] = [];
	for (const [key, raw] of Object.entries(errors as Record<string, unknown>)) {
		const node = raw as Record<string, unknown> | undefined;
		if (!node || typeof node !== "object") {
			continue;
		}
		const path = prefix ? `${prefix}.${key}` : key;
		// A leaf carries a message/type; anything else is a nested group to walk into.
		if (typeof node.message === "string" || typeof node.type === "string") {
			paths.push(path);
		} else {
			paths.push(...validationFieldPaths(node, path));
		}
	}
	return paths;
}
